const EDITABLE_TAG_NAMES = ['INPUT', 'TEXTAREA'];

/**
 * Opt-out marker for fields that look editable but only accept a fixed key
 * set, so they must not swallow global shortcuts while focused — e.g. the
 * in-call DTMF keypad input, which takes digits only and would otherwise
 * make the keypad shortcut unable to close the keypad it just opened.
 */
export const ALLOW_GLOBAL_SHORTCUTS_ATTRIBUTE = 'data-allow-global-shortcuts';

/**
 * Whether `target` is an element that accepts free-form text entry
 * (`<input>`, `<textarea>`, `[contenteditable]`, or `[role="textbox"]`),
 * unless it opts out via {@link ALLOW_GLOBAL_SHORTCUTS_ATTRIBUTE}.
 *
 * Used to suppress global keyboard shortcuts while the user is typing,
 * since modifier chords like Alt+Shift can still produce characters in
 * text fields on some OS/keyboard-layout combinations (notably macOS).
 */
export const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.closest(`[${ALLOW_GLOBAL_SHORTCUTS_ATTRIBUTE}]`)) {
    return false;
  }

  if (EDITABLE_TAG_NAMES.includes(target.tagName)) {
    return true;
  }

  if (target.isContentEditable) {
    return true;
  }

  // jsdom (unit tests) doesn't compute `isContentEditable`, so also check
  // the attribute directly — this also covers editable ancestors.
  if (target.closest('[contenteditable]:not([contenteditable="false"])')) {
    return true;
  }

  return target.getAttribute('role') === 'textbox';
};
